import React, {useState} from "react"

import {DepositAccount, Wallet} from "../../../resources/account"
import {useAccessToken} from "../../../services/auth"

import {useAsyncResult} from "../../../hooks/useAsyncResult"
import {Address} from "../../../resources/common"
import {CheckPayment, getClosureCheckPayment} from "../../../resources/checkPayments"
import {getCustomer, IndividualCustomer} from "../../../resources/customer"
import {isCustomerBeneficialOwner} from "../../../resources/customerBeneficialOwner"
import {isCustomerOfficer} from "../../../resources/customerOfficer"
import {ChangeMailingAddressModal} from "./ChangeMailingAddress"
import {NewClosureCheckModal} from "./NewClosureCheck"
import {findStopPayments, StopPayment, StopPaymentStatus} from "../../../resources/stopPayments"
import {ClosureCheckErrorModal} from "./ClosureCheckError"
import Loading from "../../../components/Loading/Loading"

export type BusinessPayeeOption = {
	value: string
	label: string
	payeeName: string
}

const BO_ROLE_LABEL = "(BO)"
const OFFICER_ROLE_LABEL = "(Officer)"

interface CreateClosureCheckModalProps {
	account: DepositAccount | Wallet
	customerId: string
	customerAddress: Address
	payeeName: string
	accountHolders?: IndividualCustomer[]
	isBusinessAccount?: boolean
	closeModal: () => void
	onSuccess: () => void
}

export interface CheckPaymentWithStopPayment extends CheckPayment {
	stopPaymentStatus?: StopPaymentStatus
}

function markChecks(checks: CheckPayment[], stopPayments: StopPayment[]): CheckPaymentWithStopPayment[] {
	return checks.map((check) => {
		const stopPayment = stopPayments.find(
			(stop) => stop.type === "checkStopPayment" && stop.relationships.checkPayment?.data?.id.toString() === check.id
		)
		return {
			...check,
			stopPaymentStatus: stopPayment?.attributes.status,
		}
	})
}

function BusinessClosureCheckContent({
	account,
	customerId,
	accessToken,
	payeeName,
	accountHolders,
	updatedAddress,
	openChangeAddressModal,
	checksWithStopPayment,
	closeModal,
	onSuccess,
}: {
	account: DepositAccount | Wallet
	customerId: string
	accessToken: string
	payeeName: string
	accountHolders?: IndividualCustomer[]
	updatedAddress: Address
	openChangeAddressModal: () => void
	checksWithStopPayment: CheckPaymentWithStopPayment[]
	closeModal: () => void
	onSuccess: () => void
}) {
	const businessCustomer = useAsyncResult(
		() => getCustomer(accessToken, customerId, ["beneficialOwner", "officer"]),
		[customerId]
	)

	const businessFetchError = businessCustomer.match(
		() => null,
		() => null,
		(e) => <ClosureCheckErrorModal error={e.errors[0].title} closeModal={closeModal} onSuccess={onSuccess} />
	)
	if (businessFetchError) return businessFetchError

	const businessPending = businessCustomer.match(
		() => true,
		() => false,
		() => false
	)
	if (businessPending) return <Loading title="Loading payee options…" />

	const businessPayeeOptions: BusinessPayeeOption[] = businessCustomer.match(
		() => [],
		(result) => {
			const opts: BusinessPayeeOption[] = []
			if (result.data.type === "businessCustomer") {
				const name = result.data.attributes.name.replace(/\s+/g, " ").trim()
				opts.push({value: `business:${result.data.id}`, label: result.data.attributes.name, payeeName: name})
			}
			if (result.data.type === "businessWalletCustomer") {
				const name = result.data.attributes.businessName.replace(/\s+/g, " ").trim()
				opts.push({value: `business:${result.data.id}`, label: result.data.attributes.businessName, payeeName: name})
			}
			if (result.included) {
				for (const item of result.included) {
					if (isCustomerBeneficialOwner(item)) {
						const name = `${item.attributes.fullName.first} ${item.attributes.fullName.last}`
							.replace(/\s+/g, " ")
							.trim()
						const pctLabel =
							item.attributes.percentage != null ? ` (BO ${item.attributes.percentage}%)` : ` ${BO_ROLE_LABEL}`
						opts.push({value: `bo:${item.id}`, label: `${name}${pctLabel}`, payeeName: name})
					} else if (isCustomerOfficer(item)) {
						const name = `${item.attributes.fullName.first} ${item.attributes.fullName.last}`
							.replace(/\s+/g, " ")
							.trim()
						opts.push({value: `officer:${item.id}`, label: `${name} ${OFFICER_ROLE_LABEL}`, payeeName: name})
					}
				}
			}
			return opts
		},
		() => []
	)

	return (
		<NewClosureCheckModal
			account={account}
			customerId={customerId}
			payeeName={payeeName}
			accountHolders={accountHolders}
			businessPayeeOptions={businessPayeeOptions}
			updatedAddress={updatedAddress}
			openChangeAddressModal={openChangeAddressModal}
			closureChecks={checksWithStopPayment}
			closeModal={closeModal}
			onSuccess={onSuccess}
		/>
	)
}

export function CreateClosureCheckModal({
	account,
	customerId,
	customerAddress,
	payeeName,
	accountHolders,
	isBusinessAccount,
	closeModal,
	onSuccess,
}: CreateClosureCheckModalProps) {
	const accessToken = useAccessToken()
	const accountId = account.id
	const previousClosureChecks = useAsyncResult(() => getClosureCheckPayment(accessToken, customerId, accountId), [])
	const stopPayments = useAsyncResult(
		() =>
			findStopPayments(
				accessToken,
				0,
				10000,
				undefined,
				customerId,
				accountId,
				undefined,
				undefined,
				undefined,
				undefined,
				undefined,
				undefined,
				undefined,
				undefined,
				[StopPaymentStatus.Active, StopPaymentStatus.Disabled]
			),
		[]
	)

	const checksWithStopPayment = previousClosureChecks.match(
		() => [],
		(checks) =>
			stopPayments.match(
				() => [],
				(stops) => markChecks(checks.data, stops.data),
				() => []
			),
		() => []
	)
	const [isChangeAddressModalOpen, setIsChangeAddressModalOpen] = useState(false)
	const [updatedAddress, setUpdatedAddress] = useState(customerAddress)
	const openChangeAddressModal = () => setIsChangeAddressModalOpen(true)
	const closeChangeAddressModal = () => setIsChangeAddressModalOpen(false)
	const handleAddressSave = (newAddress: Address) => {
		setUpdatedAddress(newAddress)
		closeChangeAddressModal()
	}

	if (isChangeAddressModalOpen) {
		return (
			<ChangeMailingAddressModal
				address={updatedAddress}
				closeModal={closeChangeAddressModal}
				onSuccess={handleAddressSave}
			/>
		)
	}

	// Handle error in closure checks fetch
	const closureChecksError = previousClosureChecks.match(
		() => null,
		() => null,
		(e) => <ClosureCheckErrorModal error={e.errors[0].title} closeModal={closeModal} onSuccess={onSuccess} />
	)
	if (closureChecksError) return closureChecksError

	if (isBusinessAccount) {
		return (
			<BusinessClosureCheckContent
				account={account}
				customerId={customerId}
				accessToken={accessToken}
				payeeName={payeeName}
				accountHolders={accountHolders}
				updatedAddress={updatedAddress}
				openChangeAddressModal={openChangeAddressModal}
				checksWithStopPayment={checksWithStopPayment}
				closeModal={closeModal}
				onSuccess={onSuccess}
			/>
		)
	}

	return (
		<NewClosureCheckModal
			account={account}
			customerId={customerId}
			payeeName={payeeName}
			accountHolders={accountHolders}
			updatedAddress={updatedAddress}
			openChangeAddressModal={openChangeAddressModal}
			closureChecks={checksWithStopPayment}
			closeModal={closeModal}
			onSuccess={onSuccess}
		/>
	)
}
